import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/axios';
import { useAuth } from '../context/AuthContext';
import './ProductSelection.css';

// Helper buiten component zodat deze altijd beschikbaar is (voorkomt i.filter is not a function)
function toArray(x) {
  try {
    if (Array.isArray(x)) return Array.prototype.slice.call(x);
    if (x != null && typeof x[Symbol.iterator] === 'function') return Array.from(x);
  } catch (_) {}
  return [];
}
function safeFilter(arr, fn) {
  const a = toArray(arr);
  return Array.prototype.filter.call(a, fn);
}
// Unique key for a product entry: prefer _uid (for loaded quotations with possible duplicate ids), fall back to id
function pkey(p) { return p._uid || p.id; }

const ProductSelection = ({ onProductsSelected, onBack, initialOfferRequestId, initialSelectedProducts }) => {
  const { user, selectedSubscriberId } = useAuth();
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(() => Array.isArray(initialSelectedProducts) ? initialSelectedProducts : []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [offerRequests, setOfferRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const appliedInitialRequestId = useRef(null);
  const [pendingOfferRequestMeta, setPendingOfferRequestMeta] = useState(null);

  // Sync selectedProducts when the parent resets initialSelectedProducts (e.g. company switch)
  useEffect(() => {
    setSelectedProducts(Array.isArray(initialSelectedProducts) ? initialSelectedProducts : []);
  }, [initialSelectedProducts]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/products');
      const raw = response?.data;
      const list = Array.isArray(raw) ? Array.from(raw) : [];
      setProducts(list);
    } catch (err) {
      setError(
        selectedSubscriberId || user?.subscriberId
          ? 'Producten laden mislukt. Controleer de koppeling met Odoo in Instellingen of synchroniseer producten.'
          : 'Selecteer eerst een bedrijf (bovenaan) of maak een bedrijf aan in Instellingen.'
      );
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, [selectedSubscriberId]);

  const loadOfferRequests = async () => {
    if (!selectedSubscriberId && user?.role !== 'admin') return;
    setLoadingRequests(true);
    try {
      const res = await api.get('/api/aanvragen?unused=true', {
        headers: selectedSubscriberId ? { 'X-Subscriber-Id': selectedSubscriberId } : {}
      });
      setOfferRequests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Offer requests load error:', err);
      setOfferRequests([]);
    } finally {
      setLoadingRequests(false);
    }
  };

  useEffect(() => {
    if (selectedSubscriberId) loadOfferRequests();
    else setOfferRequests([]);
  }, [selectedSubscriberId]);

  // Vanaf Aanvragendashboard: producten voorselecteren (gebruiker moet zelf op Doorgaan klikken)
  useEffect(() => {
    const productArr = toArray(products);
    if (!initialOfferRequestId || productArr.length === 0) return;
    if (appliedInitialRequestId.current === initialOfferRequestId) return;
    appliedInitialRequestId.current = initialOfferRequestId;
    // Geen cancelled-flag: de ref-guard voorkomt dubbele uitvoering
    (async () => {
      try {
        const res = await api.get(`/api/aanvragen/${initialOfferRequestId}`, {
          headers: selectedSubscriberId ? { 'X-Subscriber-Id': selectedSubscriberId } : {}
        });
        const request = res.data;
        if (!request) return;
        console.log('[ApplyRequest] aanvraag products:', JSON.stringify(request.products));
        console.log('[ApplyRequest] loaded product IDs (first 5):', productArr.slice(0, 5).map(p => p.id));
        const resolved = [];
        for (const item of request.products || []) {
          if (item.product_id) {
            const p = productArr.find(pr => String(pr.id) === String(item.product_id));
            if (p) {
              resolved.push({ ...p, quantity: item.quantity ?? 1, customDescription: item.description || '' });
            } else {
              console.warn('[ApplyRequest] product_id niet gevonden in lijst:', item.product_id);
              // Product ID niet gevonden: neem op als ad-hoc met beschikbare info
              resolved.push({
                id: item.product_id,
                name: item.name || `Product (${String(item.product_id).slice(0, 8)}…)`,
                description: item.description || '',
                price: parseFloat(item.price) || 0,
                quantity: item.quantity ?? 1,
                customDescription: ''
              });
            }
          } else if (item.name) {
            resolved.push({
              id: `adhoc-${resolved.length}-${Date.now()}`,
              name: item.name,
              description: item.description || '',
              price: parseFloat(item.price) || 0,
              quantity: item.quantity ?? 1,
              customDescription: ''
            });
          }
        }
        if (resolved.length === 0) return;
        setSelectedProducts(resolved);
        const meta = {
          offerRequestId: request.id,
          customer: request.customer,
          aanvrager: request.aanvrager,
          opmerkingen: request.opmerkingen || ''
        };
        setPendingOfferRequestMeta(meta);
        try { await api.post(`/api/aanvragen/${request.id}/use`); } catch (_) {}
      } catch (err) {
        console.error('Apply initial offer request error:', err);
        appliedInitialRequestId.current = null;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOfferRequestId, products, selectedSubscriberId]);

  const applyOfferRequest = async (request) => {
    const reqProducts = toArray(request.products);
    const productArr = toArray(products);
    const resolved = [];
    for (const item of reqProducts) {
      if (item.product_id) {
        const p = productArr.find(pr => String(pr.id) === String(item.product_id));
        if (p) {
          resolved.push({ ...p, quantity: item.quantity ?? 1, customDescription: item.description || '' });
        } else {
          resolved.push({
            id: item.product_id,
            name: item.name || `Product (${String(item.product_id).slice(0, 8)}…)`,
            description: item.description || '',
            price: parseFloat(item.price) || 0,
            quantity: item.quantity ?? 1,
            customDescription: ''
          });
        }
      } else if (item.name) {
        resolved.push({
          id: `adhoc-${resolved.length}-${Date.now()}`,
          name: item.name,
          description: item.description || '',
          price: parseFloat(item.price) || 0,
          quantity: item.quantity ?? 1,
          customDescription: ''
        });
      }
    }
    if (resolved.length === 0) {
      setError('Deze aanvraag bevat geen producten.');
      return;
    }
    setSelectedProducts(resolved);
    setError(null);
    const meta = {
      offerRequestId: request.id,
      customer: request.customer,
      aanvrager: request.aanvrager,
      opmerkingen: request.opmerkingen || ''
    };
    setPendingOfferRequestMeta(meta);
    try {
      await api.post(`/api/aanvragen/${request.id}/use`);
    } catch (_) {}
  };

  const handleSyncFromOdoo = async () => {
    if (!selectedSubscriberId && user?.role === 'admin') {
      setError('Selecteer eerst een bedrijf in de header.');
      return;
    }
    try {
      setSyncing(true);
      setError(null);
      const res = await api.post('/api/products/sync');
      await loadProducts();
      setError(res.data?.message ? `✓ ${res.data.message}` : null);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Odoo-sync mislukt. Controleer Odoo-gegevens bij het bedrijf (Instellingen of Supabase).');
    } finally {
      setSyncing(false);
    }
  };

  const toggleProduct = (product) => {
    setSelectedProducts((prev) => {
      const list = toArray(prev);
      const exists = list.find((p) => p.id === product.id);
      if (exists) {
        return Array.prototype.filter.call(list, (p) => pkey(p) !== pkey(exists));
      }
      return [...list, { ...product, _uid: `${product.id}-${Date.now()}`, quantity: 1, customDescription: '' }];
    });
  };

  const updateQuantity = (productKey, quantity) => {
    setSelectedProducts((prev) => {
      const list = toArray(prev);
      return Array.prototype.map.call(list, (p) =>
        pkey(p) === productKey ? { ...p, quantity: Math.max(1, parseInt(quantity, 10) || 1) } : p
      );
    });
  };

  const updateDescription = (productKey, description) => {
    setSelectedProducts((prev) => {
      const list = toArray(prev);
      return Array.prototype.map.call(list, (p) =>
        pkey(p) === productKey ? { ...p, customDescription: description } : p
      );
    });
  };

  // Verberg producten die oude offerte-PDF's zijn (Offertebijlage, Voorstel Ploko, etc.)
  const isOldOfferPdf = (name) => {
    if (!name || typeof name !== 'string') return false;
    const n = name.toLowerCase();
    return n.includes('offertebijlage') || (n.includes('voorstel') && /\d{4,}|s\d{5}/i.test(name));
  };

  const productList = toArray(products);
  const filteredProducts = safeFilter(productList, (p) => p && !isOldOfferPdf(p.name));
  const filteredProducts2 = safeFilter(
    filteredProducts,
    (p) =>
      (p.name && String(p.name).toLowerCase().includes(searchTerm.toLowerCase())) ||
      (p.description && String(p.description).toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const safeSelected = toArray(selectedProducts);

  const handleContinue = () => {
    if (safeSelected.length === 0) {
      setError('Selecteer minimaal één product');
      return;
    }
    onProductsSelected(safeSelected, pendingOfferRequestMeta || undefined);
  };

  if (loading) {
    return <div className="loading">Producten laden...</div>;
  }

  return (
    <div className="product-selection">
      <div className="container">
        <div className="header product-selection-header">
          <div>
            <h1 className="logo">OfferteRunner</h1>
            <p className="tagline">Stap 1: Selecteer Producten</p>
          </div>
          {(selectedSubscriberId || user?.role !== 'admin') && (
            <button
              type="button"
              className="btn btn-secondary product-sync-btn"
              onClick={handleSyncFromOdoo}
              disabled={syncing}
            >
              {syncing ? 'Synchroniseren…' : 'Sync producten'}
            </button>
          )}
        </div>

        {error && <div className={`error ${error.startsWith('✓') ? 'success' : ''}`}>{error}</div>}

        <div className="card">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Zoek producten..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="products-grid">
            {filteredProducts2.map(product => {
              const isSelected = safeSelected.find(p => p.id === product.id);
              return (
                <div
                  key={product.id}
                  className={`product-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggleProduct(product)}
                >
                  <div className="product-checkbox">
                    <input
                      type="checkbox"
                      checked={!!isSelected}
                      onChange={() => toggleProduct(product)}
                    />
                  </div>
                  <div className="product-info">
                    <h3>{product.name}</h3>
                    {product.description && (
                      <p className="product-description">{product.description}</p>
                    )}
                    {product.category && (
                      <span className="product-category">{product.category}</span>
                    )}
                    <div className="product-price">
                      € {parseFloat(product.price || 0).toFixed(2).replace('.', ',')}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {safeSelected.length > 0 && (
            <div className="selected-products-panel">
              <h3>Geselecteerde Producten ({safeSelected.length})</h3>
              {safeSelected.map(product => (
                <div key={pkey(product)} className="selected-product-item">
                  <div className="selected-product-info">
                    <strong>{product.name}</strong>
                    <div className="quantity-control">
                      <label>Aantal:</label>
                      <input
                        type="number"
                        min="1"
                        value={product.quantity}
                        onChange={(e) => updateQuantity(pkey(product), e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="description-control">
                      <label>Beschrijving (optioneel):</label>
                      <textarea
                        value={product.customDescription}
                        onChange={(e) => updateDescription(pkey(product), e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Aangepaste beschrijving voor deze offerte"
                        rows={2}
                      />
                    </div>
                    <div className="product-total">
                      Totaal: € {((parseFloat(product.price) || 0) * (parseInt(product.quantity, 10) || 1)).toFixed(2).replace('.', ',')}
                    </div>
                  </div>
                  <button
                    className="btn-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleProduct(product);
                    }}
                  >
                    Verwijder
                  </button>
                </div>
              ))}
              <div className="grand-total">
                <strong>
                  Totaal: € {safeSelected.reduce((sum, p) =>
                    sum + (parseFloat(p.price) || 0) * (parseInt(p.quantity, 10) || 1), 0
                  ).toFixed(2).replace('.', ',')}
                </strong>
              </div>
            </div>
          )}

          <div className="action-buttons" data-demo-step="demo-step-1">
            <button
              className="btn btn-primary"
              onClick={handleContinue}
              disabled={safeSelected.length === 0}
            >
              Doorgaan met {safeSelected.length} product{safeSelected.length !== 1 ? 'en' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductSelection;
